import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';

import { IonicModule, ViewWillEnter } from '@ionic/angular';
import { ToastrService } from 'ngx-toastr';
import { filter, take, throwError } from 'rxjs';
import { ResourcesEndpointEnum } from 'src/app/core/enums/resources-endpoint.enum';
import { WindowSizes } from 'src/app/core/enums/window-sizes.enum';
import { GroupInterface } from 'src/app/core/interfaces/group.interface';
import { ReportConfig } from 'src/app/core/interfaces/report-config';
import { UserInterface } from 'src/app/core/interfaces/user.interface';
import { FrontendConfig } from 'src/app/core/interfaces/frontend-config';
import { ResourceService } from 'src/app/core/services/resource.service';
import { ReportCardComponent } from 'src/app/shared/components/report-card/report-card.component';
import { DashboardV2Component } from 'src/app/modules/home/pages/dashboard-v2/dashboard-v2.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfigService } from 'src/app/core/services/config.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { ConfigurationStore } from 'src/app/core/stores/configuration.store';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
    IonicModule,
    ReportCardComponent,
    DashboardV2Component,
    TranslateModule
]
})
export class DashboardComponent implements OnInit, ViewWillEnter {
  user!: UserInterface;
  groups: GroupInterface[] = [];
  showLoadingReports = false;
  reports!: ReportConfig[];
  sortedReports!: ReportConfig[];
  configuration!: FrontendConfig;
  public windowwidth = innerWidth;
  onmobileView = false;
  windowSizes = WindowSizes;
  illustrationPhoto: string;
  logoUrl!: string;
  isV2 = false;
  portalReady = false;

  constructor(
    public resourceService: ResourceService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private configService: ConfigService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private configurationStore: ConfigurationStore
  ) {
    this.illustrationPhoto = this.configService.getSettings("msal.blobStorageIllustrationPhotos");
  }

  async ngOnInit() {
    this.initializeLogoUrl();

    this.configurationStore.config$.pipe(
      filter((config: FrontendConfig | null) => config !== null)
    ).subscribe((config: FrontendConfig | null) => {
      this.configuration = config!;
      // console.log('DashboardComponent: Configuration loaded:', this.configuration);
      // this.configuration.PortalVersion = 'V2'; // for testing purpose, remove this line when the portal is ready for production

      this.authService.getUserData().pipe(
        filter((user: UserInterface | null) => !!user),
        take(1)
      ).subscribe((user: UserInterface) => {
        this.user = user;
        // V2 is only available for Premium; otherwise fall back to V1
        this.isV2 = this.configuration?.PortalVersion === 'V2' && !!user.IsPremium;
        this.portalReady = true;
        if (!this.isV2) {
          this.groups = this.user?.Groups;
          if (this.groups?.length !== 0 && this.groups !== null) {
            this.showLoadingReports = true;
            this.getPinnedFavoriteReports();
          }
        }
        this.cdr.detectChanges();
      });
    });

    if (this.windowwidth < this.windowSizes.MaxMobileWidth) {
      this.onmobileView = true;
    }
  }

  ionViewWillEnter(): void {
    this.initializeLogoUrl();
  }

  private initializeLogoUrl(): void {
    const timestamp = Date.now();
    const baseUrl = `${this.illustrationPhoto}/HomeScreenLogo.png`;
    this.logoUrl = `${baseUrl}?t=${timestamp}&r=${Math.random()}&nocache=1`;
    this.preloadLogo(this.logoUrl);
  }

  private preloadLogo(url: string): void {
    const img = new Image();
    img.onload = () => { this.logoUrl = url; };
    img.onerror = () => {
      this.logoUrl = `${this.illustrationPhoto}/HomeScreenLogo.png`;
    };
    img.src = url;
  }

  getPinnedFavoriteReports() {
    this.resourceService.get<ReportConfig[]>(ResourcesEndpointEnum.GET_PINNED_FAVORITES_REPORTS)
      .pipe(filter((reports: ReportConfig[] | null) => reports !== null))
      .subscribe({
        next: (reports: ReportConfig[] | null) => {
          this.showLoadingReports = false;
          this.reports = reports!;
          this.sortReports(this.reports);
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.translateService.get('Error when loading reports').subscribe(t => {
            this.toastrService.error(t);
          });
          this.showLoadingReports = false;
          throwError(() => err);
        },
        complete: () => {
          this.showLoadingReports = false;
        }
      });
  }

  private sortReports(reports: ReportConfig[]) {
    this.sortedReports = reports.sort((a: ReportConfig, b: ReportConfig) => {
      if (!a.Pinned && b.Pinned) return 1;
      if (a.Pinned && !b.Pinned) return -1;
      if ((a.Title ?? '') > (b.Title ?? '')) return 1;
      if ((b.Title ?? '') > (a.Title ?? '')) return -1;
      return 0;
    });
  }
}
